// Shared type definitions for the Self Assessment filing wizard and draft system

export type WizardStepId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8

export interface SAAddress {
  line1: string
  line2: string
  line3: string
  line4: string
  postcode: string
  country: string
}

export interface PersonalDetailsForm {
  firstName: string
  middleName: string
  lastName: string
  telephoneNumber: string
  emailAddress: string
  companyType: string
  address: SAAddress
  isAddressCorrect: 'yes' | 'no'
  taxPayerResidency: 'english' | 'scottish' | 'welsh'
  dateOfBirth: string
  maritalStatus: string
  isBlind: 'yes' | 'no'
  blindLocalAuthority: string
  hasStudentLoan: 'yes' | 'no'
  studentLoanPlanType?: '1' | '2' | '4' | undefined
  hasPostgraduateLoan: 'yes' | 'no'
}

export interface TailorReturnForm {
  wasEmployee: 'yes' | 'no'
  wasSelfEmployed: 'yes' | 'no'
  hasProperty: 'yes' | 'no'
  hasDividends: 'yes' | 'no'
  taxPayerResidency: 'english' | 'scottish' | 'welsh'
  numberOfEmployments: string
  employerNames: string[]
}

export interface EmployerDetailsForm {
  employerId: string
  employerName: string
  payeReference: string
  payrollId: string
  totalPay: string
  totalTax: string
  startDate: string
  cessationDate: string
  payFrequency: 'monthly' | 'weekly'
  occupationalPension: 'yes' | 'no'
  studentLoanDeductions: { uglDeductionAmount: string; pglDeductionAmount: string }
  benefitsInKind: Record<string, string>
  offPayrollWorker: 'yes' | 'no'
}

export interface OverpaidTaxForm {
  noUKBank: boolean
  bankName: string
  accountHolder: string
  sortCode: string
  accountNumber: string
}

export interface SelfEmploymentAnnualData {
  adjustments: {
    includedNonTaxableProfits: string
    basisAdjustment: string
    overlapReliefUsed: string
    accountingAdjustment: string
    averagingAdjustment: string
  }
  allowances: {
    annualInvestmentAllowance: string
    otherCapitalAllowance: string
    businessPremisesRenovationAllowance: string
    costOfReplacingDomesticGoods: string
    zeroEmissionGoodsVehicleAllowance: string
    enhancedCapitalAllowance: string
    allowanceOnSales: string
    capitalAllowanceMainPool: string
    capitalAllowanceSpecialRatePool: string
    tradingIncomeAllowance: string
  }
  nonFinancials: {
    class4NicExempt: 'yes' | 'no'
    exemptionCode: string
    payeEmployed: 'yes' | 'no'
  }
}

export interface SelfEmploymentPeriodData {
  periodId: string
  fromDate: string
  toDate: string
  turnover: string
  otherIncome: string
  costOfGoods: string
  staffCosts: string
  travelCosts: string
  premisesCosts: string
}

export interface PropertyPeriod {
  periodId: string
  fromDate: string
  toDate: string
  rentIncome: string
  premiums: string
  premisesRunningCosts: string
  repairsMaintenance: string
  legalManagementCosts: string
}

export const benefitsInKindFields = [
  { key: 'accommodation', label: 'Accommodation' },
  { key: 'assets', label: 'Assets' },
  { key: 'assetTransfer', label: 'Asset transfer' },
  { key: 'beneficialLoan', label: 'Beneficial loan' },
  { key: 'car', label: 'Car' },
  { key: 'carFuel', label: 'Car fuel' },
  { key: 'educationalServices', label: 'Educational services' },
  { key: 'entertaining', label: 'Entertaining' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'medicalInsurance', label: 'Medical insurance' },
  { key: 'telephone', label: 'Telephone' },
  { key: 'service', label: 'Service' },
  { key: 'taxableExpenses', label: 'Payrolled benefits which affect your student loan repayments' },
  { key: 'van', label: 'Van' },
  { key: 'vanFuel', label: 'Van fuel' },
  { key: 'mileage', label: 'Mileage' },
  { key: 'nonQualifyingRelocationExpenses', label: 'Non-qualifying relocation expenses' },
  { key: 'nurseryPlaces', label: 'Nursery places' },
  { key: 'otherItems', label: 'Other items' },
  { key: 'paymentsOnEmployeesBehalf', label: 'Payments on employees behalf' },
  { key: 'personalIncidentalExpenses', label: 'Personal incidental expenses' },
  { key: 'qualifyingRelocationExpenses', label: 'Qualifying relocation expenses' },
  { key: 'employerProvidedProfessionalSubscriptions', label: 'Employer provided professional subscriptions' },
  { key: 'employerProvidedServices', label: 'Employer provided services' },
  { key: 'incomeTaxPaidByDirector', label: 'Income tax paid by director' },
  { key: 'travelAndSubsistence', label: 'Travel and subsistence' },
  { key: 'vouchersAndCreditCards', label: 'Vouchers and credit cards' },
  { key: 'nonCash', label: 'Non-cash' },
] as const

export type BenefitInKindKey = (typeof benefitsInKindFields)[number]['key']

export const defaultBenefitsInKind = benefitsInKindFields.reduce(
  (acc, field) => {
    acc[field.key] = ''
    return acc
  },
  {} as Record<BenefitInKindKey, string>
)

// Draft data structure for persisting form state to localStorage
export interface SelfAssessmentDraft {
  draftKey: string
  nino: string
  utr: string
  taxYear: string
  flow: 'sa' | 'traditional'
  planSlug: string
  savedAt: string
  currentStep: number

  // API-derived IDs
  assessmentId: string | null
  saReturnId: string | null
  businessId: string | null
  calculationId: string | null

  // Form data
  personalDetails: PersonalDetailsForm
  tailorReturn: TailorReturnForm
  employerDetailsList: EmployerDetailsForm[]
  selfEmploymentReportingType?: 'quarterly' | 'annual'
  selfEmploymentPeriods?: SelfEmploymentPeriodData[]
  selfEmploymentAnnualData: SelfEmploymentAnnualData
  propertyPeriods: PropertyPeriod[]
  dividendsIncome: { ukDividends: string; otherUkDividends: string }
  overpaidTax: OverpaidTaxForm
}
